import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "lucide-react";
import { useSyncExternalStore } from "react";
import { cn } from "../lib/cn";
import {
  clearToasts,
  dismissToast,
  getToasts,
  subscribeToasts,
  toast,
  type Toast as ToastData,
  type ToastVariant,
} from "../lib/toastStore";

export { toast, dismissToast, clearToasts };
export type { ToastData, ToastVariant };
export type { ToastAction, ToastOptions } from "../lib/toastStore";

/** Subscribe to the toast list. `toast()` itself needs no hook and works outside React. */
export function useToast() {
  const toasts = useSyncExternalStore(subscribeToasts, getToasts, getToasts);
  return { toasts, toast, dismiss: dismissToast, clear: clearToasts };
}

const icons: Record<ToastVariant, React.ReactNode> = {
  default: <Info className="size-4 text-fg-muted" />,
  success: <CircleCheck className="size-4 text-success" />,
  warning: <TriangleAlert className="size-4 text-warning" />,
  danger: <CircleAlert className="size-4 text-danger" />,
};

function ToastItem({ item }: { item: ToastData }) {
  const variant = item.variant ?? "default";
  return (
    <div
      data-slot="toast"
      data-variant={variant}
      role={variant === "danger" || variant === "warning" ? "alert" : "status"}
      className="pointer-events-auto flex w-80 max-w-[calc(100vw-2rem-env(safe-area-inset-left,0px)-env(safe-area-inset-right,0px))] items-start gap-2.5 rounded-lg border border-border bg-surface-raised p-3 text-sm text-fg shadow-lg transition-[opacity,translate] duration-base ease-enter starting:translate-y-2 starting:opacity-0"
    >
      <span className="mt-0.5 shrink-0">{icons[variant]}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p data-slot="toast-title" className="font-medium">
          {item.title}
        </p>
        {item.description !== undefined && (
          <p data-slot="toast-description" className="text-fg-muted">
            {item.description}
          </p>
        )}
        {item.action !== undefined && (
          <button
            type="button"
            data-slot="toast-action"
            className="mt-1 self-start rounded-sm text-accent hover:underline focus-ring"
            onClick={() => {
              item.action?.onClick();
              dismissToast(item.id);
            }}
          >
            {item.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        data-slot="toast-close"
        aria-label="Dismiss"
        onClick={() => dismissToast(item.id)}
        className="-m-1 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-fg-faint transition-colors duration-fast hover:bg-fg/6 hover:text-fg focus-ring"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

export interface ToasterProps {
  className?: string;
}

/** Mount once near the app root. Renders the toast stack in the bottom-right corner. */
export function Toaster({ className }: ToasterProps) {
  const { toasts } = useToast();
  return (
    <section
      data-slot="toaster"
      aria-label="Notifications"
      className={cn(
        "pointer-events-none fixed right-[calc(1rem+env(safe-area-inset-right,0px))] bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] z-toast flex flex-col items-end gap-2",
        className,
      )}
    >
      {toasts.map((item) => (
        <ToastItem key={item.id} item={item} />
      ))}
    </section>
  );
}
